/**
 * Gluon page: Mobile Home Layout editor (SDUI).
 *
 * Both dispatcher.home and driver.home are section-driven: reorder and toggle
 * any section, and place campaigns (promoBanner — an uploaded banner image that
 * opens a link or a details page, plus a schedule window and on-off) anywhere in
 * the list. Saves into the
 * `mobile_home_layouts` collection via `/v1/gluon/mobile-home-layout`; a PUBLISHED
 * change reaches a device the next time the screen is focused, within the 5-min
 * cache TTL.
 *
 * Mirrors the visual style + app-search UX of MobileFeaturesPage.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import Alert from '@mui/joy/Alert'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Card from '@mui/joy/Card'
import Chip from '@mui/joy/Chip'
import CircularProgress from '@mui/joy/CircularProgress'
import FormControl from '@mui/joy/FormControl'
import FormLabel from '@mui/joy/FormLabel'
import IconButton from '@mui/joy/IconButton'
import Input from '@mui/joy/Input'
import Sheet from '@mui/joy/Sheet'
import Snackbar from '@mui/joy/Snackbar'
import Stack from '@mui/joy/Stack'
import Switch from '@mui/joy/Switch'
import Tab from '@mui/joy/Tab'
import TabList from '@mui/joy/TabList'
import Tabs from '@mui/joy/Tabs'
import Typography from '@mui/joy/Typography'
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { ClientApplication, MOBILE_DEEPLINK_PATHS, mobileSchemeUrl } from '@numeo/types-core'
import {
    HomeScreenId,
    HOME_SCREENS,
    MOBILE_HOME_PLAN_TIERS,
    MOBILE_HOME_PLAN_TIER_LABELS,
    MobileHomeSegment,
    PROMO_TITLE_MAX,
    PROMO_DETAIL_HEADING_MAX,
    PROMO_DETAIL_BODY_MAX,
    PROMO_CTA_LABEL_MAX,
    PROMO_ASPECT_RATIO_LABEL,
    PROMO_ROTATION_DEFAULT_INTERVAL_MINUTES,
    PROMO_ROTATION_GROUP_MAX,
    PROMO_ROTATION_MAX_INTERVAL_MINUTES,
    PROMO_ROTATION_MIN_INTERVAL_MINUTES,
    PROMO_IMAGE_MIN_WIDTH_PX,
    PROMO_IMAGE_RECOMMENDED_WIDTH_PX,
    PROMO_IMAGE_RECOMMENDED_HEIGHT_PX,
    validatePromoImageDimensions,
    clampPromoRotationInterval,
    defaultLayoutForScreen,
    homeSectionLabelsForScreen,
    isFeatureGatedDeeplink,
    isGateableSectionType,
    parsePromoPageSlug,
    campaignSectionTypeForScreen,
    type LoadsPromoTab,
} from '@numeo/types'
import Select from '@mui/joy/Select'
import Option from '@mui/joy/Option'
import { apiErrorMessage, mobileHomeLayoutApi, type HomeLayoutGetResponse } from 'api/mobileHomeLayout'
import { HomeLayoutPreview } from './HomeLayoutPreview'
import { SegmentEditor } from './SegmentEditor'
import { MarkdownField } from './MarkdownField'
import { TutorialsEditor, tutorialDraftsOf, tutorialsProblem, type TutorialDraft } from './TutorialsEditor'
import { layoutFromRows, rowsFromLayout, type EditorRow } from './layoutRows'
import { PromoCardFields } from './PromoCardFields'
import { isMobileHomeOnly } from 'utils/roles'

type Scope = 'global' | 'application' | 'plan' | 'segment'

/** A brand-new segment starts with no rules — so it matches nobody until the
 *  operator says who it is for, rather than defaulting to "everyone". */
const emptySegment = (id: string): MobileHomeSegment => ({ id, name: '', rules: [], priority: 100 })

interface SnackbarState {
    open: boolean
    message: string
    color: 'success' | 'danger'
}

/** Friendly labels for the Screen selector. */
const SCREEN_LABELS: Record<HomeScreenId, string> = {
    'dispatcher.home': 'Dispatcher Home',
    'driver.home': 'Driver Home',
    'dispatcher.loads': 'Loads board (promo cards)',
}

/** Mirrors the server-side cap in `gluon/mobile-assets/router.ts`. */
const PROMO_IMAGE_MAX_MB = 5

/**
 * Plain-English rendering of a `visibleWhen` key.
 *
 * The raw keys (`inbox.hasUnread`) are contract identifiers, not copy — an
 * operator deciding whether to reorder a section shouldn't have to decode them.
 */
const VISIBLE_WHEN_LABELS: Record<string, string> = {
    always: 'always',
    'plan.isFree': 'the user is on the free plan',
    'plan.isPaid': 'the user is on a paid plan',
    'profile.incomplete': "the user's profile is incomplete",
    'profile.complete': "the user's profile is complete",
    'inbox.hasUnread': 'there are unread broker emails',
    'search.limited': 'the user has hit the search limit',
    'driver.isOwnerOperator': 'the driver is an owner-operator',
}

/** A campaign's row title: its own copy, so several are told apart at a glance. */
function promoRowTitle(row: EditorRow): string {
    const title = String((row.section.props as Record<string, unknown> | undefined)?.title ?? '').trim()
    return title || 'Untitled campaign'
}

/** A row's trimmed rotation group, or '' when it opted out of rotation. */
function rotationGroupOf(row: EditorRow): string {
    return typeof row.section.rotationGroup === 'string' ? row.section.rotationGroup.trim() : ''
}

/**
 * Read a picked file's pixel dimensions in the browser, before upload.
 *
 * No dependency needed — decoding into an `Image` is enough, and it lets an
 * off-spec asset be rejected instantly instead of after a round trip. The object
 * URL is revoked either way so a rejected pick doesn't leak.
 */
function readImageDimensions(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
        const objectUrl = URL.createObjectURL(file)
        const img = new Image()
        img.onload = () => {
            URL.revokeObjectURL(objectUrl)
            resolve({ width: img.naturalWidth, height: img.naturalHeight })
        }
        img.onerror = () => {
            URL.revokeObjectURL(objectUrl)
            reject(new Error('That file could not be read as an image.'))
        }
        img.src = objectUrl
    })
}

export default function MobileHomeLayoutPage() {
    const [screen, setScreen] = useState<HomeScreenId>('dispatcher.home')
    const [scope, setScope] = useState<Scope>('global')
    const mobileHomeOnly = useMemo(isMobileHomeOnly, [])

    // Application picker (only used in application scope).
    const [searchQuery, setSearchQuery] = useState('')
    const [searching, setSearching] = useState(false)
    const [results, setResults] = useState<ClientApplication[]>([])
    const [selectedApp, setSelectedApp] = useState<ClientApplication | null>(null)
    const [selectedPlan, setSelectedPlan] = useState<string>('')

    // Segment picker (only used in segment scope). `segmentId` keys the layout
    // document; `segment` is the audience stored inline on it.
    const [segmentId, setSegmentId] = useState<string>('')
    const [segmentIdInput, setSegmentIdInput] = useState<string>('')
    const [segment, setSegment] = useState<MobileHomeSegment | null>(null)

    const [rows, setRows] = useState<EditorRow[]>([])
    /** Tutorial videos whose duration text does not read as a length; Publish waits for them. */
    const [badDurationIds, setBadDurationIds] = useState<ReadonlySet<string>>(new Set())
    const [baseline, setBaseline] = useState<string>('') // JSON of the loaded layout, for dirty-check
    const [isDefault, setIsDefault] = useState(false)
    const [meta, setMeta] = useState<HomeLayoutGetResponse | null>(null)
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', color: 'success' })
    /** Monotonic load counter — only the newest `loadLayout` may commit state. */
    const loadGenerationRef = useRef(0)
    /** Fallback uniqueness for new campaign ids where `crypto.randomUUID` is absent. */
    const promoSeqRef = useRef(0)
    /** Section id whose artwork is uploading — disables the other upload buttons. */
    const [uploadingRow, setUploadingRow] = useState<string | null>(null)

    // Debounced app search.
    useEffect(() => {
        if (scope !== 'application' || !searchQuery.trim()) {
            setResults([])
            return
        }
        const timer = setTimeout(async () => {
            setSearching(true)
            try {
                setResults(await mobileHomeLayoutApi.searchApplications(searchQuery.trim()))
            } catch {
                setResults([])
                setSnackbar({ open: true, message: 'Search failed', color: 'danger' })
            } finally {
                setSearching(false)
            }
        }, 300)
        return () => clearTimeout(timer)
    }, [searchQuery, scope])

    async function loadLayout(nextScope: Scope, nextScreen: HomeScreenId, app: ClientApplication | null, planTier?: string, nextSegmentId?: string) {
        if (nextScope === 'application' && !app) return
        if (nextScope === 'plan' && !planTier) return
        if (nextScope === 'segment' && !nextSegmentId) return
        // Only the most recent load may commit. Flipping Screen or Scope twice
        // quickly can let the first (slower) response resolve last, leaving the
        // editor showing — and about to SAVE — the previous selection's sections
        // under the newly-selected key. The server's `layout.screen !== key.screen`
        // check catches the cross-screen case, but a scope-to-scope race writes
        // the wrong layout silently, so it has to be stopped here.
        const generation = ++loadGenerationRef.current
        const isStale = () => generation !== loadGenerationRef.current
        setLoading(true)
        try {
            const res = await mobileHomeLayoutApi.get(nextScope, { screen: nextScreen, projectName: app?.projectName, planTier, segmentId: nextSegmentId })
            if (isStale()) return
            // Edit the DRAFT (falls back to published/default when none exists).
            const nextRows = rowsFromLayout(res.draft, nextScreen)
            setRows(nextRows)
            setBaseline(JSON.stringify(layoutFromRows(nextRows, nextScreen)))
            setIsDefault(res.isDefault)
            setMeta(res)
            // Stored audience, or a fresh empty one for a segment id that has no
            // document yet.
            if (nextScope === 'segment' && nextSegmentId) setSegment(res.segment ?? emptySegment(nextSegmentId))
            else setSegment(null)
        } catch (err) {
            if (isStale()) return
            setSnackbar({ open: true, message: `Load failed: ${apiErrorMessage(err)}`, color: 'danger' })
        } finally {
            if (!isStale()) setLoading(false)
        }
    }

    // Load the layout on mount + whenever the screen or scope changes. Reloads
    // the currently-selected app/plan for the new screen so switching Screen
    // shows that screen's sections.
    useEffect(() => {
        if (scope === 'global') void loadLayout('global', screen, null)
        else if (scope === 'application' && selectedApp) void loadLayout('application', screen, selectedApp)
        else if (scope === 'plan' && selectedPlan) void loadLayout('plan', screen, null, selectedPlan)
        else if (scope === 'segment' && segmentId) void loadLayout('segment', screen, null, undefined, segmentId)
        else {
            setRows([])
            setBaseline('')
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scope, screen])

    function selectApp(app: ClientApplication) {
        setSelectedApp(app)
        void loadLayout('application', screen, app)
    }

    function selectPlan(tier: string) {
        setSelectedPlan(tier)
        void loadLayout('plan', screen, null, tier)
    }

    /** Open (or start) the layout for an audience id. Normalized to the slug the
     *  schema accepts, so a typed "Owner Operators" opens `owner-operators`
     *  rather than bouncing off a 400 the operator has to decode. */
    function selectSegment(rawId: string) {
        const id = rawId
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9-]+/g, '-')
            .replace(/^-+|-+$/g, '')
        if (!id) return
        setSegmentId(id)
        setSegmentIdInput(id)
        void loadLayout('segment', screen, null, undefined, id)
    }

    function toggle(index: number) {
        setRows((prev) => {
            const toggled = prev.map((r, i) => (i === index ? { ...r, enabled: !r.enabled } : r))
            // Tutorials belong right under the quick tiles (Figma Spot 10638:114645); switching the
            // row on puts it there instead of at the bottom, where a never-shown singleton is listed.
            const row = toggled[index]
            if (row.type !== 'tutorials' || !row.enabled) return toggled
            const rest = toggled.filter((_, i) => i !== index)
            const tilesIndex = rest.findIndex((r) => r.type === 'quickTiles')
            return tilesIndex === -1 ? toggled : [...rest.slice(0, tilesIndex + 1), row, ...rest.slice(tilesIndex + 1)]
        })
    }

    function setDurationValidity(id: string, valid: boolean) {
        setBadDurationIds((prev) => {
            if (prev.has(id) !== valid) return prev
            const next = new Set(prev)
            if (valid) next.delete(id)
            else next.add(id)
            return next
        })
    }

    /** Keyed by section id, so an edit lands on the right row even after the rows moved. */
    function setTutorialItems(sectionId: string, update: (items: TutorialDraft[]) => TutorialDraft[]) {
        setRows((prev) =>
            prev.map((r) =>
                r.section.id === sectionId
                    ? { ...r, section: { ...r.section, props: { ...(r.section.props ?? {}), items: update(tutorialDraftsOf(r.section.props)) } } }
                    : r
            )
        )
    }

    function move(index: number, dir: -1 | 1) {
        setRows((prev) => {
            const next = [...prev]
            const target = index + dir
            if (target < 0 || target >= next.length) return prev
            ;[next[index], next[target]] = [next[target], next[index]]
            return next
        })
    }

    function reset() {
        const nextRows = rowsFromLayout(defaultLayoutForScreen(screen), screen)
        setRows(nextRows)
    }

    function addPromo() {
        // `promo-${Date.now()}` collided: two clicks inside the same millisecond
        // (a double-click, or a StrictMode double-invoke in dev) produced the same
        // id. That id is the React key in BOTH the editor list and the app's
        // renderer, so edits to one banner could land on the other.
        const id = typeof crypto !== 'undefined' && crypto.randomUUID ? `promo-${crypto.randomUUID()}` : `promo-${Date.now()}-${++promoSeqRef.current}`
        // Seeded incomplete on purpose: a campaign needs its content and a destination, and the save gate names what
        // is still missing. Loads campaigns are cards placed after a load; Home ones are banners.
        const type = campaignSectionTypeForScreen(screen)
        const props = type === 'promoCard' ? { title: 'New card', position: 1 } : { title: 'New promo', tapBehavior: 'link' }
        setRows((prev) => [...prev, { type, enabled: true, section: { type, id, props } }])
    }

    function removeRow(index: number) {
        setRows((prev) => prev.filter((_, i) => i !== index))
    }

    /**
     * Upload the banner artwork and store the returned PUBLIC url on the row.
     * The input is reset afterwards so re-picking the same file still fires
     * `onChange` — otherwise a failed upload can't be retried with the same image.
     */
    async function uploadPromoImage(sectionId: string, event: React.ChangeEvent<HTMLInputElement>) {
        const file = event.target.files?.[0]
        event.target.value = ''
        if (!file) return
        setUploadingRow(sectionId)
        try {
            // Read the real dimensions BEFORE uploading, and reject here with the
            // expected-vs-actual numbers. App Store Connect's failure mode — upload,
            // wait, then a post-hoc "dimensions are wrong" with no target — is the
            // thing to avoid; the browser gives us this for free.
            const { width, height } = await readImageDimensions(file)
            const problem = validatePromoImageDimensions(width, height)
            if (problem) {
                setSnackbar({ open: true, message: problem, color: 'danger' })
                return
            }
            const url = await mobileHomeLayoutApi.uploadPromoImage(file)
            // Derive the ratio from the ARTWORK rather than asking the operator to
            // type one. The container then always matches the image, so the banner
            // is never cropped — which is the whole point of this validation.
            setPromoImage(sectionId, url, width / height)
            setSnackbar({ open: true, message: `Image uploaded (${width}×${height})`, color: 'success' })
        } catch (err) {
            setSnackbar({ open: true, message: `Upload failed: ${apiErrorMessage(err)}`, color: 'danger' })
        } finally {
            setUploadingRow(null)
        }
    }

    /**
     * Store the artwork url and its derived aspect ratio in one update.
     *
     * Keyed by section id, NOT array index: an upload takes seconds, and reorder
     * and Remove stay enabled while it is in flight. An index captured at pick
     * time can point at a different row by the time the response lands —
     * overwriting another campaign's artwork, or attaching promo props to a
     * native section, which then fails `.strict()` at save.
     */
    function setPromoImage(sectionId: string, url: string, aspectRatio: number) {
        setRows((prev) =>
            prev.map((r) =>
                r.section.id === sectionId
                    ? {
                          ...r,
                          // Rounded: the schema takes a number, and full float
                          // precision in a stored payload is noise.
                          section: { ...r.section, props: { ...(r.section.props ?? {}), imageUrl: url, aspectRatio: Number(aspectRatio.toFixed(4)) } },
                      }
                    : r
            )
        )
    }

    // An emptied field must be REMOVED, not stored as ''. The shared schema
    // validates promo props strictly, so a lingering `targetPlan: ''` (or any
    // cleared enum) would fail validation on save — and an empty subtitle is
    // absence, not content. Mirrors updateSchedule below.
    function updateProp(index: number, key: string, value: string) {
        setRows((prev) =>
            prev.map((r, i) => {
                if (i !== index) return r
                const props = { ...(r.section.props ?? {}) }
                if (value) props[key] = value
                else delete props[key]
                return { ...r, section: { ...r.section, props } }
            })
        )
    }

    /** Set or clear a Loads card prop. Keyed by section id, like `setPromoImage`: the icon upload lands after rows may have moved. */
    function updateCardProp(sectionId: string, key: string, value: string | number | LoadsPromoTab[] | undefined) {
        setRows((prev) =>
            prev.map((r) => {
                if (r.section.id !== sectionId) return r
                const props = { ...(r.section.props ?? {}) }
                if (value === undefined || value === '' || (typeof value === 'number' && Number.isNaN(value))) delete props[key]
                else props[key] = value
                return { ...r, section: { ...r.section, props } }
            })
        )
    }

    /** Update one field of a `details` campaign's detail payload. */
    function updateDetail(index: number, key: string, value: string) {
        setRows((prev) =>
            prev.map((r, i) => {
                if (i !== index) return r
                const props = { ...(r.section.props ?? {}) }
                const detail = { ...((props.detail as Record<string, unknown>) ?? {}) }
                if (value) detail[key] = value
                else delete detail[key]
                props.detail = detail
                return { ...r, section: { ...r.section, props } }
            })
        )
    }

    /**
     * Switch a campaign's tap behaviour, dropping the payload the other mode owns.
     * The schema rejects `linkUrl` on a `details` campaign and vice versa, so
     * leaving the old field behind would turn a behaviour switch into a save-time
     * 400 the operator has no obvious way to clear.
     */
    function setTapBehavior(index: number, behavior: string) {
        setRows((prev) =>
            prev.map((r, i) => {
                if (i !== index) return r
                const props: Record<string, unknown> = { ...(r.section.props ?? {}), tapBehavior: behavior }
                if (behavior === 'link') delete props.detail
                else delete props.linkUrl
                return { ...r, section: { ...r.section, props } }
            })
        )
    }

    // Clearing both dates must REMOVE `schedule`, not leave `{}` behind. An empty
    // object is truthy, so the preview would keep showing a "scheduled" chip for a
    // campaign with no window, and `dirty` would compare `"schedule":{}` against a
    // baseline that has no such key — reporting unsaved changes that don't exist.
    function updateSchedule(index: number, key: 'start' | 'end', value: string) {
        setRows((prev) =>
            prev.map((r, i) => {
                if (i !== index) return r
                const next = { ...(r.section.schedule ?? {}), [key]: value || undefined }
                const hasBound = !!(next.start || next.end)
                const section = { ...r.section, schedule: hasBound ? next : undefined }
                if (!hasBound) delete section.schedule
                return { ...r, section }
            })
        )
    }

    /**
     * Set a campaign's rotation group, or clear it.
     *
     * Clearing the group must also drop the interval: the shared schema rejects
     * an interval with no group (it would rotate nothing while reading like a
     * working setting), so leaving it behind turns the next Publish into a
     * validation error on a field the operator just emptied.
     */
    function updateRotationGroup(index: number, value: string) {
        setRows((prev) =>
            prev.map((r, i) => {
                if (i !== index) return r
                const group = value.trim()
                const section = { ...r.section, rotationGroup: group || undefined }
                if (!group) {
                    delete section.rotationGroup
                    delete section.rotationIntervalMinutes
                }
                return { ...r, section }
            })
        )
    }

    /** Set a campaign's rotation interval; an empty field falls back to the default. */
    function updateRotationInterval(index: number, value: string) {
        setRows((prev) =>
            prev.map((r, i) => {
                if (i !== index) return r
                const minutes = Number.parseInt(value, 10)
                const section = { ...r.section, rotationIntervalMinutes: Number.isFinite(minutes) ? minutes : undefined }
                if (!Number.isFinite(minutes)) delete section.rotationIntervalMinutes
                return { ...r, section }
            })
        )
    }

    /**
     * Campaigns sharing a rotation group, keyed by group name.
     *
     * Drives both the per-row hint ("1 of 3") and the interval-conflict warning.
     * Built from ENABLED rows only — a disabled row is not published, so counting
     * it would promise a rotation the app will never perform.
     */
    const rotationGroups = useMemo(() => {
        const groups = new Map<string, EditorRow[]>()
        for (const r of rows) {
            const group = rotationGroupOf(r)
            if (!r.enabled || !isGateableSectionType(r.type) || !group) continue
            const members = groups.get(group)
            if (members) members.push(r)
            else groups.set(group, [r])
        }
        return groups
    }, [rows])

    const dirty = useMemo(() => baseline !== '' && JSON.stringify(layoutFromRows(rows, screen)) !== baseline, [rows, baseline, screen])
    const canEdit =
        scope === 'global' || (scope === 'application' && !!selectedApp) || (scope === 'plan' && !!selectedPlan) || (scope === 'segment' && !!segmentId)

    // 1-based positions of enabled campaigns with no title. `title` is required by
    // the shared schema, so saving one bounces back as a raw 400 that names a
    // section INDEX ("sections.0.props.title") — useless for finding the row in a
    // long list. Catch it here and point at the row instead.
    // Numbered over the ENABLED rows only, so the position quoted here is the same
    // one the row badge shows. Indexing the raw array instead counts hidden rows —
    // and `rowsFromLayout` appends every not-present section as a disabled row, so
    // hidden rows above a campaign is the normal state, not an edge case. The whole
    // point of this message is to save the operator from decoding
    // `sections.0.props.title`, which it fails at if it names a number no row has.
    const untitledPromoRows = useMemo(
        () =>
            rows
                .filter((r) => r.enabled)
                .map((r, i) => ({ r, position: i + 1 }))
                .filter(({ r }) => isGateableSectionType(r.type) && !String(r.section.props?.title ?? '').trim())
                .map(({ position }) => position),
        [rows]
    )
    // An out-of-range rotation interval is a hard reject at the schema, not a
    // silent clamp — `rotationIntervalMinutes` carries a zod `.min()`/`.max()`
    // that `parseHomeLayoutStrict` enforces on every Save Draft. Left uncaught
    // here, the operator gets a raw 400 naming a section index instead of the
    // row and value that caused it — the same failure mode `untitledPromoRows`
    // exists to avoid for a missing title.
    const outOfRangeRotationRows = useMemo(
        () =>
            rows
                .filter((r) => r.enabled)
                .map((r, i) => ({ r, position: i + 1 }))
                .filter(({ r }) => {
                    if (!isGateableSectionType(r.type) || !rotationGroupOf(r)) return false
                    const minutes = r.section.rotationIntervalMinutes
                    return typeof minutes === 'number' && (minutes < PROMO_ROTATION_MIN_INTERVAL_MINUTES || minutes > PROMO_ROTATION_MAX_INTERVAL_MINUTES)
                })
                .map(({ position }) => position),
        [rows]
    )
    // A segment with no rules matches nobody, and one with no name is
    // unidentifiable in the scope picker later. Both bounce off the server's zod
    // schema as a raw 400, so catch them here and say which is missing.
    const segmentError =
        scope === 'segment' && segment
            ? !segment.name.trim()
                ? 'Give this segment a name.'
                : !segment.rules.length
                  ? 'Add at least one audience rule — a segment with no rules matches nobody.'
                  : ''
            : ''
    const tutorialsRow = rows.find((r) => r.type === 'tutorials' && r.enabled)
    const tutorialsError = tutorialsRow ? tutorialsProblem(tutorialDraftsOf(tutorialsRow.section.props), badDurationIds) : ''
    const emptyLayoutError = rows.some((r) => r.enabled)
        ? ''
        : 'Keep at least one section visible. Use the mobile SDUI kill switch to hide the whole Home layout.'

    const blockingError = untitledPromoRows.length
        ? `Campaign${untitledPromoRows.length > 1 ? 's' : ''} at position ${untitledPromoRows.join(', ')} need${untitledPromoRows.length > 1 ? '' : 's'} a title.`
        : outOfRangeRotationRows.length
          ? `Campaign${outOfRangeRotationRows.length > 1 ? 's' : ''} at position ${outOfRangeRotationRows.join(', ')} ${outOfRangeRotationRows.length > 1 ? 'have' : 'has'} a rotation interval outside ${PROMO_ROTATION_MIN_INTERVAL_MINUTES}–${PROMO_ROTATION_MAX_INTERVAL_MINUTES} min.`
          : tutorialsError || emptyLayoutError || segmentError

    /**
     * What to do next, in one line.
     *
     * The draft → approve → publish flow is only legible from which buttons are
     * enabled, which is a puzzle rather than an instruction — especially "why is
     * Publish greyed out?" (answer: unsaved edits). Say it instead.
     */
    const nextStepHint = blockingError
        ? blockingError
        : dirty
          ? 'Unsaved changes — Save draft to keep them.'
          : meta?.hasDraft
            ? 'Draft saved. Publish to send it to devices.'
            : isDefault
              ? 'Nothing published — devices are showing the app’s built-in layout.'
              : 'Published and live.'

    // Memoized: the preview re-renders a phone frame with every section, and an
    // inline `layoutFromRows(...)` produced a new array identity on every
    // keystroke in the campaign form.
    const previewSections = useMemo(() => layoutFromRows(rows, screen).sections.filter((section) => section.enabled !== false), [rows, screen])

    const keyOpts = () => ({
        screen,
        projectName: selectedApp?.projectName,
        planTier: selectedPlan || undefined,
        segmentId: segmentId || undefined,
    })

    function reload() {
        return loadLayout(
            scope,
            screen,
            scope === 'application' ? selectedApp : null,
            scope === 'plan' ? selectedPlan : undefined,
            scope === 'segment' ? segmentId : undefined
        )
    }

    /** Run a governance mutation, then reload so published/draft/history refresh. */
    async function runMutation(fn: () => Promise<void>, okMsg: string) {
        if (!canEdit) return
        if (blockingError) {
            setSnackbar({ open: true, message: blockingError, color: 'danger' })
            return
        }
        setSaving(true)
        try {
            await fn()
            await reload()
            setSnackbar({ open: true, message: okMsg, color: 'success' })
        } catch (err) {
            setSnackbar({ open: true, message: apiErrorMessage(err), color: 'danger' })
        } finally {
            setSaving(false)
        }
    }

    const saveDraft = () =>
        runMutation(() => mobileHomeLayoutApi.saveDraft(scope, layoutFromRows(rows, screen), keyOpts(), segment ?? undefined), 'Draft saved (not yet live)')
    const approve = () => runMutation(() => mobileHomeLayoutApi.approve(scope, keyOpts()), 'Draft approved')
    const publish = () => runMutation(() => mobileHomeLayoutApi.publish(scope, keyOpts()), 'Published — live on next app refresh')
    const rollback = (index: number) => runMutation(() => mobileHomeLayoutApi.rollback(scope, index, keyOpts()), 'Rolled back — live on next app refresh')

    /**
     * A labelled band inside the campaign editor.
     *
     * The campaign form is ~9 controls deep. Flat, they all read at the same
     * weight and "Ends (UTC)" looks as central as the artwork — so it's grouped
     * into the three questions actually being answered: what it looks like, what
     * happens on tap, and when it runs.
     */
    const promoGroup = (label: string, children: React.ReactNode) => (
        <Box>
            <Typography level="body-xs" textTransform="uppercase" sx={{ letterSpacing: '0.06em', fontWeight: 'lg', color: 'text.tertiary', mb: 0.75 }}>
                {label}
            </Typography>
            <Stack spacing={1}>{children}</Stack>
        </Box>
    )

    /**
     * Explain what this row's rotation setting will actually do.
     *
     * Rotation is the one campaign setting whose effect is invisible in the
     * preview — the editor can only ever draw one moment, so "these three take
     * turns" has to be said in words or not at all. The three states worth
     * calling out are a group of one (authored, but rotates nothing), an
     * out-of-range interval (blocks saving — see `outOfRangeRotationRows`), and
     * members disagreeing on the interval (the first member's value wins).
     */
    const rotationHint = (row: EditorRow) => {
        const group = rotationGroupOf(row)
        if (!group) {
            return (
                <Typography level="body-xs" color="neutral">
                    Not rotating — this campaign shows whenever it is in its schedule window, alongside any others.
                </Typography>
            )
        }
        const members = rotationGroups.get(group) ?? []
        if (members.length < 2) {
            return (
                <Typography level="body-xs" color="warning">
                    Only campaign in “{group}” — it will always show. Put another campaign in the same group for them to take turns.
                </Typography>
            )
        }
        const position = members.findIndex((m) => m.section.id === row.section.id) + 1
        const leadInterval = members[0].section.rotationIntervalMinutes
        // `clampPromoRotationInterval` describes a READ-time backstop — data that
        // reached storage some other way. The write path this editor actually
        // uses (`saveDraft` → `parseHomeLayoutStrict`) enforces the same bound as
        // a hard zod `.min()`/`.max()`, so an out-of-range value here is a save
        // that FAILS, not one that gets silently normalized. `effective` is only
        // ever shown for an in-range lead value; out-of-range is caught by
        // `outOfRangeRotationRows` → `blockingError` before Save Draft is enabled.
        const effective = clampPromoRotationInterval(leadInterval)
        const own = row.section.rotationIntervalMinutes
        const outOfRange = typeof own === 'number' && (own < PROMO_ROTATION_MIN_INTERVAL_MINUTES || own > PROMO_ROTATION_MAX_INTERVAL_MINUTES)
        const overridden = typeof own === 'number' && own !== leadInterval
        return (
            <Stack spacing={0.25}>
                <Typography level="body-xs" color="neutral">
                    {position} of {members.length} in “{group}” — one shows at a time, switching every {effective} min.
                </Typography>
                {outOfRange && (
                    <Typography level="body-xs" color="danger">
                        {PROMO_ROTATION_MIN_INTERVAL_MINUTES}–{PROMO_ROTATION_MAX_INTERVAL_MINUTES} min only — this value will block saving. Under{' '}
                        {PROMO_ROTATION_MIN_INTERVAL_MINUTES} min can't be shown on device anyway: the app caches its layout for{' '}
                        {PROMO_ROTATION_MIN_INTERVAL_MINUTES} minutes.
                    </Typography>
                )}
                {overridden && !outOfRange && (
                    <Typography level="body-xs" color="warning">
                        The group runs on {effective} min — the first campaign in “{group}” sets the pace for everyone in it.
                    </Typography>
                )}
            </Stack>
        )
    }

    /** Rotation and schedule, shared by Home banners and Loads cards. */
    const campaignTimingFields = (i: number, row: EditorRow) => (
        <>
            {promoGroup(
                'Rotation',
                <>
                    <Stack direction="row" spacing={1}>
                        <FormControl size="sm" sx={{ flex: 1 }}>
                            <FormLabel>Rotation group</FormLabel>
                            <Input
                                size="sm"
                                placeholder="e.g. launch — leave empty to always show"
                                value={typeof row.section.rotationGroup === 'string' ? row.section.rotationGroup : ''}
                                onChange={(e) => updateRotationGroup(i, e.target.value)}
                                slotProps={{ input: { maxLength: PROMO_ROTATION_GROUP_MAX } }}
                            />
                        </FormControl>
                        <FormControl size="sm" sx={{ flex: 1 }} disabled={!rotationGroupOf(row)}>
                            <FormLabel>Rotate every (minutes)</FormLabel>
                            <Input
                                size="sm"
                                type="number"
                                placeholder={String(PROMO_ROTATION_DEFAULT_INTERVAL_MINUTES)}
                                value={typeof row.section.rotationIntervalMinutes === 'number' ? String(row.section.rotationIntervalMinutes) : ''}
                                onChange={(e) => updateRotationInterval(i, e.target.value)}
                                slotProps={{ input: { min: PROMO_ROTATION_MIN_INTERVAL_MINUTES, max: PROMO_ROTATION_MAX_INTERVAL_MINUTES } }}
                            />
                        </FormControl>
                    </Stack>
                    {rotationHint(row)}
                </>
            )}
            {promoGroup(
                'Schedule',
                <>
                    <Stack direction="row" spacing={1}>
                        <FormControl size="sm" sx={{ flex: 1 }}>
                            <FormLabel>Starts (UTC)</FormLabel>
                            <Input
                                size="sm"
                                type="date"
                                value={(row.section.schedule?.start ?? '').slice(0, 10)}
                                onChange={(e) => updateSchedule(i, 'start', e.target.value)}
                            />
                        </FormControl>
                        <FormControl size="sm" sx={{ flex: 1 }}>
                            <FormLabel>Ends (UTC, inclusive)</FormLabel>
                            <Input
                                size="sm"
                                type="date"
                                value={(row.section.schedule?.end ?? '').slice(0, 10)}
                                onChange={(e) => updateSchedule(i, 'end', e.target.value)}
                            />
                        </FormControl>
                    </Stack>
                    {/* The window is evaluated in UTC, not the operator's local time: a
                    campaign ending on the 9th runs through 23:59 UTC on the 9th, which
                    is 20:00 on the 9th in US-Eastern. Stated here because the dates are
                    authored locally and the skew is otherwise invisible. */}
                    <Typography level="body-xs" color="neutral">
                        Schedule is evaluated in UTC. Leave a date empty for an open-ended window.
                    </Typography>
                </>
            )}
        </>
    )

    /** The editable fields for a Loads promo card, then its rotation and schedule. */
    const promoCardFields = (i: number, row: EditorRow) => (
        <Stack spacing={2}>
            <PromoCardFields
                props={(row.section.props ?? {}) as Record<string, unknown>}
                onText={(key, value) => updateCardProp(row.section.id, key, value)}
                onPosition={(position) => updateCardProp(row.section.id, 'position', position)}
                onTabs={(tabs) => updateCardProp(row.section.id, 'tabs', tabs)}
                onError={(message) => setSnackbar({ open: true, message, color: 'danger' })}
            />
            {campaignTimingFields(i, row)}
        </Stack>
    )

    /** The editable fields for a campaign (promoBanner) — artwork, tap behaviour, schedule. */
    const promoFields = (i: number, row: EditorRow) => {
        const p = (row.section.props ?? {}) as Record<string, unknown>
        const detail = (p.detail ?? {}) as Record<string, string>
        const tapBehavior = typeof p.tapBehavior === 'string' ? p.tapBehavior : 'link'
        const imageUrl = typeof p.imageUrl === 'string' ? p.imageUrl : ''
        return (
            <Stack spacing={2} sx={{ mt: 1.5 }}>
                {/* ARTWORK — the banner is the image; there is no server-driven copy or
                    colour, so the upload is the campaign's main content. */}
                {promoGroup(
                    'Artwork',
                    <>
                        <FormControl size="sm">
                            <FormLabel>Banner image</FormLabel>
                            {imageUrl ? (
                                // Shown on BOTH app backgrounds. Transparency is allowed (a
                                // banner may legitimately blend into the surface), but a PNG
                                // with alpha that reads well on white can disappear against
                                // the dark theme — and neither the operator nor the layout
                                // preview would otherwise reveal that.
                                <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                                    {[
                                        { label: 'Light', bg: '#F2F3F5' },
                                        { label: 'Dark', bg: '#0B0B0F' },
                                    ].map(({ label, bg }) => (
                                        <Box key={label} sx={{ flex: 1 }}>
                                            <Box
                                                component="img"
                                                src={imageUrl}
                                                alt={`Banner preview on ${label.toLowerCase()} background`}
                                                sx={{ width: '100%', borderRadius: 8, display: 'block', bgcolor: bg, objectFit: 'contain' }}
                                            />
                                            <Typography level="body-xs" color="neutral" sx={{ mt: 0.25, textAlign: 'center' }}>
                                                {label}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            ) : (
                                <Box sx={{ p: 2, mb: 1, borderRadius: 8, bgcolor: 'background.level1', textAlign: 'center' }}>
                                    <Typography level="body-xs" color="neutral">
                                        No image yet — upload one to publish this campaign.
                                    </Typography>
                                </Box>
                            )}
                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                <Button
                                    size="sm"
                                    variant="outlined"
                                    component="label"
                                    loading={uploadingRow === row.section.id}
                                    disabled={uploadingRow !== null}
                                >
                                    {imageUrl ? 'Replace image' : 'Upload image'}
                                    <input
                                        type="file"
                                        accept="image/jpeg,image/png,image/webp"
                                        hidden
                                        onChange={(e) => void uploadPromoImage(row.section.id, e)}
                                    />
                                </Button>
                                <Typography level="body-xs" color="neutral">
                                    {PROMO_ASPECT_RATIO_LABEL} — design at {PROMO_IMAGE_RECOMMENDED_WIDTH_PX}×{PROMO_IMAGE_RECOMMENDED_HEIGHT_PX}px. JPEG / PNG
                                    / WebP, max {PROMO_IMAGE_MAX_MB}MB, at least {PROMO_IMAGE_MIN_WIDTH_PX}px wide. Shown whole — never cropped.
                                </Typography>
                            </Stack>
                        </FormControl>
                        {/* Never drawn — the artwork carries the visible copy. Required so the
                    banner is announceable to a screen reader. */}
                        <FormControl size="sm">
                            <FormLabel>Accessibility label</FormLabel>
                            <Input
                                size="sm"
                                placeholder="What a screen reader announces, e.g. 'Summer sale — 20% off'"
                                value={(p.title as string) ?? ''}
                                onChange={(e) => updateProp(i, 'title', e.target.value)}
                                slotProps={{ input: { maxLength: PROMO_TITLE_MAX } }}
                            />
                        </FormControl>
                    </>
                )}
                {/* TAP BEHAVIOUR — each mode needs its own payload, and the schema
                    rejects the other one, so the form only shows the relevant fields. */}
                {promoGroup(
                    'When tapped',
                    <>
                        <FormControl size="sm">
                            <Select size="sm" value={tapBehavior} onChange={(_, v) => v && setTapBehavior(i, v as string)}>
                                <Option value="link">Open a link directly</Option>
                                <Option value="details">Show a details page (with its own button)</Option>
                            </Select>
                        </FormControl>
                        {tapBehavior === 'link' ? (
                            <FormControl size="sm">
                                <FormLabel>Link url</FormLabel>
                                <Input
                                    size="sm"
                                    placeholder={`https://… or ${mobileSchemeUrl(MOBILE_DEEPLINK_PATHS.settings)}`}
                                    value={(p.linkUrl as string) ?? ''}
                                    onChange={(e) => updateProp(i, 'linkUrl', e.target.value)}
                                />
                                {/* Warn, don't block. Reachability is decided by the user's role
                                    features (ADR-019), not by which Home they see — a driver who
                                    joined via invite-accept has dispatcher features too — so
                                    rejecting these on driver Home would kill campaigns that work
                                    for a real population. Users without the feature get the same
                                    silent no-op every deep link in the app already gives them. */}
                                {isFeatureGatedDeeplink((p.linkUrl as string) ?? '') && (
                                    <Typography level="body-xs" color="warning" sx={{ mt: 0.5 }}>
                                        This screen sits behind a role feature. Users without it will see the banner but nothing will happen when they tap it.
                                    </Typography>
                                )}
                                {parsePromoPageSlug((p.linkUrl as string) ?? '') && (
                                    <Typography level="body-xs" color="warning" sx={{ mt: 0.5 }}>
                                        App builds released before promo pages reject this link and hide the banner. Only users on a build with promo pages will
                                        see it.
                                    </Typography>
                                )}
                            </FormControl>
                        ) : (
                            <>
                                <FormControl size="sm">
                                    <FormLabel>Details heading</FormLabel>
                                    <Input
                                        size="sm"
                                        value={detail.heading ?? ''}
                                        onChange={(e) => updateDetail(i, 'heading', e.target.value)}
                                        slotProps={{ input: { maxLength: PROMO_DETAIL_HEADING_MAX } }}
                                    />
                                </FormControl>
                                <FormControl size="sm">
                                    <FormLabel>Details body</FormLabel>
                                    <MarkdownField
                                        value={detail.body ?? ''}
                                        onChange={(next) => updateDetail(i, 'body', next)}
                                        maxLength={PROMO_DETAIL_BODY_MAX}
                                    />
                                </FormControl>
                                <Stack direction="row" spacing={1}>
                                    <FormControl size="sm" sx={{ flex: 1 }}>
                                        <FormLabel>Button label</FormLabel>
                                        <Input
                                            size="sm"
                                            placeholder="e.g. Learn more"
                                            value={detail.ctaLabel ?? ''}
                                            onChange={(e) => updateDetail(i, 'ctaLabel', e.target.value)}
                                            slotProps={{ input: { maxLength: PROMO_CTA_LABEL_MAX } }}
                                        />
                                    </FormControl>
                                    <FormControl size="sm" sx={{ flex: 1 }}>
                                        <FormLabel>Button url</FormLabel>
                                        <Input
                                            size="sm"
                                            placeholder="https://…"
                                            value={detail.ctaUrl ?? ''}
                                            onChange={(e) => updateDetail(i, 'ctaUrl', e.target.value)}
                                        />
                                    </FormControl>
                                </Stack>
                            </>
                        )}
                    </>
                )}
                {/* No "Position on Home" picker: a campaign's position IS its place in
                    the section list above — move it with the up/down arrows. */}
                {campaignTimingFields(i, row)}
            </Stack>
        )
    }

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1280, mx: 'auto' }}>
            {/* HEADER — title + the one-line explanation. The longer mechanics
                (resolution order, cache TTL) moved to a dismissible-feeling hint
                below the selectors, where they're read once rather than every visit. */}
            <Stack direction="row" sx={{ alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 0.5 }}>
                <Box>
                    <Typography level="h2">Mobile Home Layout</Typography>
                    <Typography level="body-sm" color="neutral" sx={{ mt: 0.5 }}>
                        Control what appears on the mobile Home screen — section order, visibility, and marketing campaigns.
                    </Typography>
                </Box>
            </Stack>

            {/* TARGET — screen and scope are one decision ("which layout am I
                editing?"), so they sit together in a single bar rather than as two
                unrelated controls stacked down the page. */}
            <Card variant="soft" sx={{ mt: 2.5, mb: 2.5, p: 2, gap: 2 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ alignItems: { md: 'flex-end' } }}>
                    <FormControl sx={{ minWidth: 220 }}>
                        <FormLabel>Screen</FormLabel>
                        <Select value={screen} onChange={(_, v) => v && setScreen(v)}>
                            {HOME_SCREENS.map((s) => (
                                <Option key={s} value={s}>
                                    {SCREEN_LABELS[s]}
                                </Option>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl sx={{ flex: 1, minWidth: 0 }}>
                        <FormLabel>Applies to</FormLabel>
                        <Tabs value={scope} onChange={(_, v) => setScope(v as Scope)} sx={{ borderRadius: 8, bgcolor: 'transparent' }}>
                            <TabList disableUnderline sx={{ p: 0.5, borderRadius: 8, bgcolor: 'background.level2', gap: 0.5 }}>
                                <Tab value="global" disableIndicator sx={{ borderRadius: 6 }}>
                                    Everyone
                                </Tab>
                                {!mobileHomeOnly && (
                                    <Tab value="application" disableIndicator sx={{ borderRadius: 6 }}>
                                        One company
                                    </Tab>
                                )}
                                <Tab value="plan" disableIndicator sx={{ borderRadius: 6 }}>
                                    One plan tier
                                </Tab>
                                <Tab value="segment" disableIndicator sx={{ borderRadius: 6 }}>
                                    An audience
                                </Tab>
                            </TabList>
                        </Tabs>
                    </FormControl>
                </Stack>
                <Typography level="body-xs" color="neutral">
                    A device gets the most specific layout that exists: audience → company → plan tier → everyone. Published changes reach the app the next time
                    Home is opened, within 5 minutes.
                </Typography>
            </Card>

            {scope === 'application' && (
                <Card sx={{ mb: 3 }}>
                    <FormControl>
                        <FormLabel>Search company by project name</FormLabel>
                        <Input
                            placeholder="e.g. acme-trucking"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            endDecorator={searching ? <CircularProgress size="sm" /> : undefined}
                        />
                    </FormControl>
                    {results.length > 0 && (
                        <Stack spacing={1} sx={{ mt: 2 }}>
                            {results.map((app) => (
                                <Box
                                    key={app.id}
                                    onClick={() => selectApp(app)}
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 8,
                                        cursor: 'pointer',
                                        bgcolor: selectedApp?.id === app.id ? 'primary.softBg' : 'background.level1',
                                        '&:hover': { bgcolor: 'primary.softHoverBg' },
                                    }}
                                >
                                    <Typography level="title-sm">{app.projectName}</Typography>
                                </Box>
                            ))}
                        </Stack>
                    )}
                </Card>
            )}

            {scope === 'plan' && (
                <Card sx={{ mb: 3 }}>
                    <FormControl>
                        <FormLabel>Plan tier</FormLabel>
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                            {MOBILE_HOME_PLAN_TIERS.map((tier) => (
                                <Button key={tier} variant={selectedPlan === tier ? 'solid' : 'outlined'} color="primary" onClick={() => selectPlan(tier)}>
                                    {MOBILE_HOME_PLAN_TIER_LABELS[tier]}
                                </Button>
                            ))}
                        </Stack>
                    </FormControl>
                </Card>
            )}

            {scope === 'segment' && (
                <Card sx={{ mb: 3 }}>
                    <FormControl>
                        <FormLabel>Audience id</FormLabel>
                        <Input
                            placeholder="e.g. owner-operators"
                            value={segmentIdInput}
                            onChange={(e) => setSegmentIdInput(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && selectSegment(segmentIdInput)}
                            endDecorator={
                                <Button size="sm" variant="soft" disabled={!segmentIdInput.trim()} onClick={() => selectSegment(segmentIdInput)}>
                                    Open
                                </Button>
                            }
                        />
                        <Typography level="body-xs" sx={{ mt: 0.75, color: 'text.tertiary' }}>
                            Lowercase letters, numbers and dashes. Opening an id that has no layout yet starts a new one.
                        </Typography>
                    </FormControl>
                </Card>
            )}

            {scope === 'segment' && segment && <SegmentEditor segment={segment} onChange={setSegment} disabled={saving || loading} />}

            {canEdit && (
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 2, alignItems: 'flex-start' }}>
                    <Card sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography level="title-md">
                                {scope === 'global'
                                    ? 'Global layout'
                                    : scope === 'plan'
                                      ? `Layout for ${MOBILE_HOME_PLAN_TIER_LABELS[selectedPlan as keyof typeof MOBILE_HOME_PLAN_TIER_LABELS] ?? selectedPlan} plan`
                                      : scope === 'segment'
                                        ? `Layout for ${segment?.name?.trim() || segmentId}`
                                        : `Layout for ${selectedApp?.projectName}`}
                            </Typography>
                            <Stack direction="row" sx={{ gap: 1 }}>
                                {isDefault && (
                                    <Chip size="sm" color="neutral" variant="soft">
                                        Showing default (none published)
                                    </Chip>
                                )}
                                {meta?.hasDraft && (
                                    <Chip size="sm" color="warning" variant="soft">
                                        Unpublished draft
                                    </Chip>
                                )}
                            </Stack>
                        </Stack>

                        {!!blockingError && (
                            <Alert size="sm" color="danger" variant="soft" sx={{ mb: 1 }}>
                                {blockingError}
                            </Alert>
                        )}

                        {loading ? (
                            <Box sx={{ py: 4, textAlign: 'center' }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Stack spacing={1}>
                                {/* Both screens are section-driven: reorder and toggle every section,
                                    and place campaigns anywhere in the list. The dispatcher screen was
                                    campaign-only while its Home rendered natively; it no longer does. */}
                                {rows.map((row, i) => {
                                    const isPromo = isGateableSectionType(row.type)
                                    // Position among the sections that will actually render, so the
                                    // number matches what a user sees rather than counting hidden rows.
                                    const visiblePosition = rows.slice(0, i + 1).filter((r) => r.enabled).length
                                    return (
                                        <Sheet
                                            key={row.section.id}
                                            variant="outlined"
                                            sx={{
                                                p: 1.5,
                                                borderRadius: 10,
                                                // A hidden section must LOOK hidden: same row, visibly
                                                // recessed. Previously enabled and disabled rows were
                                                // pixel-identical apart from the switch.
                                                bgcolor: row.enabled ? 'background.surface' : 'background.level1',
                                                borderColor: isPromo && row.enabled ? 'primary.outlinedBorder' : 'neutral.outlinedBorder',
                                                opacity: row.enabled ? 1 : 0.62,
                                                transition: 'opacity 120ms, background-color 120ms',
                                            }}
                                        >
                                            <Stack direction="row" sx={{ alignItems: 'flex-start', gap: 1.5 }}>
                                                <Stack sx={{ gap: 0, pt: 0.25, alignItems: 'center' }}>
                                                    <IconButton size="sm" variant="plain" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move up">
                                                        <KeyboardArrowUp />
                                                    </IconButton>
                                                    <Typography level="body-xs" color="neutral" sx={{ lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                                                        {row.enabled ? visiblePosition : '–'}
                                                    </Typography>
                                                    <IconButton
                                                        size="sm"
                                                        variant="plain"
                                                        disabled={i === rows.length - 1}
                                                        onClick={() => move(i, 1)}
                                                        aria-label="Move down"
                                                    >
                                                        <KeyboardArrowDown />
                                                    </IconButton>
                                                </Stack>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                        <Typography level="title-sm">
                                                            {isPromo ? promoRowTitle(row) : (homeSectionLabelsForScreen(screen)[row.type] ?? row.type)}
                                                        </Typography>
                                                        {isPromo && (
                                                            <Chip size="sm" color="primary" variant="soft">
                                                                Campaign
                                                            </Chip>
                                                        )}
                                                        {!row.enabled && (
                                                            <Chip size="sm" color="neutral" variant="soft">
                                                                Hidden
                                                            </Chip>
                                                        )}
                                                        {row.unknown && (
                                                            <Chip size="sm" color="warning" variant="soft">
                                                                Unknown type — preserved
                                                            </Chip>
                                                        )}
                                                    </Stack>
                                                    {!isPromo && (
                                                        <Typography level="body-xs" color="neutral" sx={{ mt: 0.25 }}>
                                                            {row.section.visibleWhen
                                                                ? `Only when ${VISIBLE_WHEN_LABELS[row.section.visibleWhen] ?? row.section.visibleWhen}`
                                                                : 'Always shown'}
                                                            {row.unknown ? ' · this Gluon build has no editor for it; saved back unchanged' : ''}
                                                        </Typography>
                                                    )}
                                                    {isPromo && (row.type === 'promoCard' ? promoCardFields(i, row) : promoFields(i, row))}
                                                    {row.type === 'tutorials' && row.enabled && (
                                                        <TutorialsEditor
                                                            items={tutorialDraftsOf(row.section.props)}
                                                            onUpdate={(update) => setTutorialItems(row.section.id, update)}
                                                            onDurationValidity={setDurationValidity}
                                                        />
                                                    )}
                                                </Box>
                                                <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5 }}>
                                                    <Switch
                                                        checked={row.enabled}
                                                        onChange={() => toggle(i)}
                                                        slotProps={{ input: { 'aria-label': row.enabled ? 'Hide section' : 'Show section' } }}
                                                    />
                                                    {(isPromo || row.unknown) && (
                                                        <IconButton size="sm" variant="plain" color="danger" onClick={() => removeRow(i)} aria-label="Remove">
                                                            <DeleteOutlineIcon />
                                                        </IconButton>
                                                    )}
                                                </Stack>
                                            </Stack>
                                        </Sheet>
                                    )
                                })}
                            </Stack>
                        )}

                        <Button variant="outlined" color="primary" onClick={addPromo} disabled={saving || loading} sx={{ mt: 2, alignSelf: 'flex-start' }}>
                            + Add campaign
                        </Button>

                        {/* ACTION BAR — sticks to the bottom of the card. The section
                            list grows past a screen once a few campaigns exist, and
                            Save/Publish being scrolled off is how unsaved work gets
                            lost. A single status line states what the NEXT step is,
                            rather than leaving the operator to infer it from which
                            buttons happen to be enabled. */}
                        <Box
                            sx={{
                                position: 'sticky',
                                bottom: 0,
                                mt: 2,
                                pt: 1.5,
                                borderTop: '1px solid',
                                borderColor: 'divider',
                                bgcolor: 'background.surface',
                                zIndex: 1,
                            }}
                        >
                            <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography level="body-xs" color={dirty ? 'warning' : 'neutral'}>
                                    {nextStepHint}
                                </Typography>
                                <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
                                    <Button variant="plain" color="neutral" onClick={reset} disabled={saving || loading}>
                                        Reset to default
                                    </Button>
                                    <Button variant="outlined" onClick={saveDraft} loading={saving} disabled={!dirty || loading}>
                                        Save draft
                                    </Button>
                                    <Button variant="outlined" color="neutral" onClick={approve} disabled={saving || loading || !meta?.hasDraft}>
                                        Approve
                                    </Button>
                                    <Button color="success" onClick={publish} loading={saving} disabled={loading || dirty || !meta?.hasDraft}>
                                        Publish
                                    </Button>
                                </Stack>
                            </Stack>
                        </Box>
                    </Card>
                    <Box sx={{ width: { xs: '100%', lg: 320 }, flexShrink: 0, position: { lg: 'sticky' }, top: 16, alignSelf: { lg: 'flex-start' } }}>
                        <HomeLayoutPreview sections={previewSections} screen={screen} />
                    </Box>
                </Box>
            )}

            {canEdit && meta && meta.history.length > 0 && (
                <Card sx={{ mt: 2 }}>
                    <Typography level="title-sm" sx={{ mb: 1 }}>
                        Version history
                    </Typography>
                    {/* Fixed height + scroll: history holds up to
                        HOME_LAYOUT_HISTORY_LIMIT (10) entries, and letting that grow
                        inline pushed the editor controls off-screen. ~4 rows visible
                        is enough to show the recent ones and imply the rest. */}
                    <Stack spacing={1} sx={{ maxHeight: 220, overflowY: 'auto', pr: 0.5 }}>
                        {meta.history.map((h) => (
                            <Stack
                                key={h.index}
                                direction="row"
                                sx={{
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    p: 1,
                                    borderRadius: 8,
                                    bgcolor: 'background.level1',
                                    // Flex children shrink by default inside a scroll
                                    // container; keep each row at its natural height.
                                    flexShrink: 0,
                                }}
                            >
                                <Typography level="body-xs" color="neutral">
                                    Published {new Date(h.publishedAt).toLocaleString()} · {h.publishedBy}
                                </Typography>
                                <Button size="sm" variant="plain" onClick={() => rollback(h.index)} disabled={saving || loading}>
                                    Restore
                                </Button>
                            </Stack>
                        ))}
                    </Stack>
                </Card>
            )}

            {scope === 'application' && !selectedApp && (
                <Alert color="neutral" variant="soft">
                    Search and select a company to edit its Home layout. Companies with no saved layout fall back to the global default.
                </Alert>
            )}

            {scope === 'plan' && !selectedPlan && (
                <Alert color="neutral" variant="soft">
                    Pick a plan tier to edit its Home layout. Users on a tier with no saved layout fall back to the global default.
                </Alert>
            )}

            <Snackbar
                open={snackbar.open}
                color={snackbar.color}
                variant="soft"
                autoHideDuration={3000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                {snackbar.message}
            </Snackbar>
        </Box>
    )
}
